// PageCustomization.jsx - Tab inside BookingSettings
// Edit spa name, phone, address, colors, deposit, etc.

import { useState, useEffect } from "react";
import { supabase } from "./supabase";

const C = {
  bg:"#f8f5f0", surface:"#ffffff", card:"#ffffff", border:"#e8e4df",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldD:"#8a6f2e",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

const inp = {width:"100%",padding:"9px 12px",background:C.light,border:"1px solid "+C.border,borderRadius:7,fontSize:13,color:C.text,fontFamily:F,outline:"none",boxSizing:"border-box"};

export default function PageCustomization() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => { load(); }, []);

  const load = async () => {
    const { data } = await supabase.from("booking_settings").select("*").eq("id", 1).single();
    const safe = data || {};
    if (!Array.isArray(safe.notification_phones)) safe.notification_phones = [];
    setSettings(safe);
  };

  const update = (key, value) => setSettings(s => ({...s, [key]: value}));

  const [newPhone, setNewPhone] = useState("");
  const addNotificationPhone = () => {
    const p = (newPhone || "").trim();
    if (!p) return;
    const list = Array.isArray(settings.notification_phones) ? settings.notification_phones : [];
    if (list.includes(p)) { setNewPhone(""); return; }
    update("notification_phones", [...list, p]);
    setNewPhone("");
  };
  const removeNotificationPhone = (idx) => {
    const list = Array.isArray(settings.notification_phones) ? settings.notification_phones : [];
    update("notification_phones", list.filter((_, i) => i !== idx));
  };

  const save = async () => {
    setSaving(true);
    const { error } = await supabase.from("booking_settings").update({
      spa_name: settings.spa_name,
      hero_subtitle: settings.hero_subtitle,
      welcome_message: settings.welcome_message,
      phone: settings.phone,
      address: settings.address,
      business_email: settings.business_email,
      footer_text: settings.footer_text,
      primary_color: settings.primary_color,
      secondary_color: settings.secondary_color,
      background_color: settings.background_color,
      logo_url: settings.logo_url,
      hero_image_url: settings.hero_image_url,
      require_deposit: settings.require_deposit,
      deposit_amount: settings.deposit_amount,
      show_prices: settings.show_prices,
      show_addons: settings.show_addons,
      min_advance_hours: settings.min_advance_hours,
      max_advance_days: settings.max_advance_days,
      notification_phones: Array.isArray(settings.notification_phones) ? settings.notification_phones : [],
      updated_at: new Date().toISOString(),
    }).eq("id", 1);
    setSaving(false);
    if (error) {
      setMsg("Error: " + error.message);
    } else {
      setMsg("Saved successfully!");
      setTimeout(() => setMsg(""), 2500);
    }
  };

  if (!settings) return <div style={{padding:30,textAlign:"center",color:C.muted}}>Loading...</div>;

  return (
    <div>
      <div style={{marginBottom:18}}>
        <div style={{fontSize:14,fontWeight:700,color:C.gold,fontFamily:S}}>Booking Page Customization</div>
        <div style={{fontSize:11,color:C.muted}}>Customize how your public booking page looks and behaves</div>
      </div>

      {msg && <div style={{padding:"10px 14px",background:msg.startsWith("Error")?C.redL:C.greenL,color:msg.startsWith("Error")?C.red:C.green,borderRadius:8,fontSize:12,fontWeight:600,marginBottom:14,border:"1px solid "+(msg.startsWith("Error")?C.red:C.green)+"40"}}>{msg}</div>}

      <Section title="Business Identity">
        <Row>
          <Field label="Spa Name *">
            <input value={settings.spa_name||""} onChange={e=>update("spa_name", e.target.value)} style={inp} placeholder="Nova Med Spa"/>
          </Field>
          <Field label="Subtitle / Tagline">
            <input value={settings.hero_subtitle||""} onChange={e=>update("hero_subtitle", e.target.value)} style={inp} placeholder="Where elevated aesthetics meet personalized care"/>
          </Field>
        </Row>
        <Field label="Welcome Message">
          <textarea value={settings.welcome_message||""} onChange={e=>update("welcome_message", e.target.value)} rows={2} style={{...inp, resize:"vertical"}}/>
        </Field>
      </Section>

      <Section title="Contact Information">
        <Row>
          <Field label="Phone (shown on booking page)">
            <input value={settings.phone||""} onChange={e=>update("phone", e.target.value)} style={inp} placeholder="(516) 543-3940"/>
          </Field>
          <Field label="Business Email">
            <input value={settings.business_email||""} onChange={e=>update("business_email", e.target.value)} style={inp} placeholder="hello@yourspa.com"/>
          </Field>
        </Row>
        <Field label="Address">
          <input value={settings.address||""} onChange={e=>update("address", e.target.value)} style={inp} placeholder="1451 Old Country Rd, Plainview NY 11803"/>
        </Field>
        <Field label="Footer Text">
          <input value={settings.footer_text||""} onChange={e=>update("footer_text", e.target.value)} style={inp} placeholder="Need help? Call us"/>
        </Field>
      </Section>

      <Section title="Branding & Colors">
        <Row>
          <Field label="Primary Color (gold/brand)">
            <ColorInput value={settings.primary_color||"#b48c3c"} onChange={v=>update("primary_color", v)}/>
          </Field>
          <Field label="Secondary Color (dark)">
            <ColorInput value={settings.secondary_color||"#1a1a2e"} onChange={v=>update("secondary_color", v)}/>
          </Field>
          <Field label="Background Color">
            <ColorInput value={settings.background_color||"#ffffff"} onChange={v=>update("background_color", v)}/>
          </Field>
        </Row>
        <Field label="🏷 Brand Logo (used in all emails — receipts, confirmations, treatment care, everything)">
          <LogoUploader value={settings.logo_url} onChange={v=>update("logo_url", v)}/>
        </Field>
        <Field label="Hero Image (large image behind the spa name on the booking page)">
          <HeroImageUploader value={settings.hero_image_url} onChange={v=>update("hero_image_url", v)}/>
        </Field>
      </Section>

      <Section title="Booking Behavior">
        <Row>
          <Field label="Show prices on booking page">
            <Toggle value={!!settings.show_prices} onChange={v=>update("show_prices", v)}/>
          </Field>
          <Field label="Show add-on offers">
            <Toggle value={settings.show_addons !== false} onChange={v=>update("show_addons", v)}/>
          </Field>
          <Field label="Require deposit">
            <Toggle value={!!settings.require_deposit} onChange={v=>update("require_deposit", v)}/>
          </Field>
        </Row>
        {settings.require_deposit && (
          <Field label="Default deposit amount ($)">
            <input type="number" value={settings.deposit_amount||0} onChange={e=>update("deposit_amount", +e.target.value)} style={{...inp, maxWidth:200}}/>
          </Field>
        )}
        <Row>
          <Field label="Min advance booking (hours)">
            <input type="number" value={settings.min_advance_hours||2} onChange={e=>update("min_advance_hours", +e.target.value)} style={inp}/>
          </Field>
          <Field label="Max advance booking (days)">
            <input type="number" value={settings.max_advance_days||60} onChange={e=>update("max_advance_days", +e.target.value)} style={inp}/>
          </Field>
        </Row>
      </Section>

      <Section title="Booking Notifications">
        <div style={{fontSize:12,color:C.muted,marginBottom:12,lineHeight:1.5}}>
          Get a text message every time someone books an appointment through your booking link. Add one or more phone numbers below — each one receives a full breakdown of the booking the moment it's made.
        </div>
        <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap"}}>
          <input
            value={newPhone}
            onChange={e=>setNewPhone(e.target.value)}
            onKeyDown={e=>{ if (e.key === "Enter") { e.preventDefault(); addNotificationPhone(); } }}
            placeholder="+1 (516) 555-0123"
            style={{...inp, flex:"1 1 220px", maxWidth:340}}
          />
          <button
            onClick={addNotificationPhone}
            style={{padding:"9px 16px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:7,fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:F}}
          >
            + Add Phone
          </button>
        </div>
        {(!settings.notification_phones || settings.notification_phones.length === 0) ? (
          <div style={{padding:14,textAlign:"center",color:C.muted,fontSize:12,background:C.light,borderRadius:8,border:"1px dashed "+C.border}}>
            No notification numbers yet — you won't receive any SMS alerts for new bookings.
          </div>
        ) : (
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {settings.notification_phones.map((p, i) => (
              <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:C.light,borderRadius:8,border:"1px solid "+C.border}}>
                <div style={{fontSize:14,color:C.text,fontWeight:600,flex:1,fontFamily:"monospace"}}>{p}</div>
                <button
                  onClick={()=>removeNotificationPhone(i)}
                  style={{padding:"5px 11px",background:C.redL,color:C.red,border:"1px solid "+C.red+"40",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:F}}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
        <div style={{marginTop:10,fontSize:10,color:C.dim,lineHeight:1.5}}>
          Tip: include the country code (e.g. +1 for US). You can add as many numbers as you like.
        </div>
      </Section>

      <button onClick={save} disabled={saving} style={{padding:"11px 24px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:8,fontSize:14,fontWeight:700,cursor:saving?"not-allowed":"pointer",fontFamily:F,opacity:saving?0.6:1}}>
        {saving?"Saving...":"💾 Save All Changes"}
      </button>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{marginBottom:24,padding:16,background:C.card,border:"1px solid "+C.border,borderRadius:10}}>
      <div style={{fontSize:12,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:14,fontFamily:F}}>{title}</div>
      {children}
    </div>
  );
}
function Row({ children }) {
  return <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:12,marginBottom:12}}>{children}</div>;
}
function Field({ label, children }) {
  return (
    <div style={{marginBottom:12}}>
      <label style={{display:"block",fontSize:11,fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:5,fontFamily:F}}>{label}</label>
      {children}
    </div>
  );
}
function ColorInput({ value, onChange }) {
  return (
    <div style={{display:"flex",gap:8,alignItems:"center"}}>
      <input type="color" value={value} onChange={e=>onChange(e.target.value)} style={{width:40,height:38,border:"1px solid "+C.border,borderRadius:6,cursor:"pointer",padding:2}}/>
      <input type="text" value={value} onChange={e=>onChange(e.target.value)} style={{...inp,flex:1,fontFamily:"monospace"}} placeholder="#b48c3c"/>
    </div>
  );
}
function LogoUploader({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { setErr("Please pick an image file"); return; }
    if (file.size > 5 * 1024 * 1024) { setErr("Logo must be under 5MB"); return; }
    setErr("");
    setUploading(true);
    try {
      const ext = (file.name.split(".").pop() || "png").toLowerCase();
      const path = `logo-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from("branding").upload(path, file, {
        cacheControl: "31536000",  // 1 year - logo doesn't change often
        upsert: true,
        contentType: file.type,
      });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from("branding").getPublicUrl(path);
      onChange(pub.publicUrl);
    } catch (e) {
      setErr(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {value && (
        <div style={{position:"relative",marginBottom:10,padding:18,background:"linear-gradient(135deg,#0a0a18,#1a1a2e)",borderRadius:10,border:"1px solid "+C.border,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <img src={value} alt="Brand logo" style={{maxWidth:200,maxHeight:120,objectFit:"contain",display:"block"}}/>
          <button type="button" onClick={()=>onChange("")} style={{position:"absolute",top:8,right:8,padding:"5px 11px",background:"rgba(0,0,0,0.7)",color:"#fff",border:"none",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:F}}>Remove</button>
        </div>
      )}
      <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
        <label style={{display:"inline-block",padding:"9px 16px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",borderRadius:7,fontSize:12,fontWeight:700,cursor:uploading?"not-allowed":"pointer",fontFamily:F,opacity:uploading?0.6:1}}>
          {uploading?"Uploading...":(value?"Replace logo":"Upload logo")}
          <input type="file" accept="image/png,image/jpeg,image/jpg,image/webp" onChange={onPick} disabled={uploading} style={{display:"none"}}/>
        </label>
        <input value={value||""} onChange={e=>onChange(e.target.value)} placeholder="...or paste a logo URL" style={{...inp,flex:"1 1 200px"}}/>
      </div>
      <div style={{fontSize:10,color:C.muted,marginTop:6,lineHeight:1.4}}>Square or wide format works. PNG with transparency recommended. The logo appears at the top of every email — receipts, appointment confirmations, treatment care reminders, etc.</div>
      {err && <div style={{fontSize:10,color:C.red,marginTop:4,fontWeight:600}}>{err}</div>}
    </div>
  );
}

function HeroImageUploader({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { setErr("Please pick an image file"); return; }
    if (file.size > 8 * 1024 * 1024) { setErr("Image must be under 8MB"); return; }
    setErr("");
    setUploading(true);
    try {
      const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
      const path = `hero-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from("provider-photos").upload(path, file, { cacheControl: "3600", upsert: false });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from("provider-photos").getPublicUrl(path);
      onChange(pub.publicUrl);
    } catch (e) {
      setErr(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {value && (
        <div style={{position:"relative",marginBottom:10,borderRadius:10,overflow:"hidden",border:"1px solid "+C.border}}>
          <img src={value} alt="Hero" style={{width:"100%",maxHeight:180,objectFit:"cover",display:"block"}}/>
          <button type="button" onClick={()=>onChange("")} style={{position:"absolute",top:8,right:8,padding:"5px 11px",background:"rgba(0,0,0,0.7)",color:"#fff",border:"none",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:F}}>Remove</button>
        </div>
      )}
      <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
        <label style={{display:"inline-block",padding:"9px 16px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",borderRadius:7,fontSize:12,fontWeight:700,cursor:uploading?"not-allowed":"pointer",fontFamily:F,opacity:uploading?0.6:1}}>
          {uploading?"Uploading...":(value?"Replace image":"Upload image")}
          <input type="file" accept="image/*" onChange={onPick} disabled={uploading} style={{display:"none"}}/>
        </label>
        <input value={value||""} onChange={e=>onChange(e.target.value)} placeholder="...or paste an image URL" style={{...inp,flex:"1 1 200px"}}/>
      </div>
      <div style={{fontSize:10,color:C.muted,marginTop:6,lineHeight:1.4}}>Wide image recommended (1600×600px). JPG/PNG up to 8MB. If empty, a gradient is used.</div>
      {err && <div style={{fontSize:10,color:C.red,marginTop:4,fontWeight:600}}>{err}</div>}
    </div>
  );
}

function Toggle({ value, onChange }) {
  return (
    <button onClick={()=>onChange(!value)} style={{padding:"7px 14px",background:value?"linear-gradient(135deg,"+C.green+",#1e6b3f)":C.light,color:value?"#fff":C.muted,border:"1px solid "+(value?C.green:C.border),borderRadius:7,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:F,minWidth:80}}>
      {value?"✓ ON":"OFF"}
    </button>
  );
}
